import type {
  IFormulationConstraint,
  TFormulationFractionConstraint,
} from "./constraint";
import {
  isFormulationFractionConstraint,
  isIngredientCountConstraint,
  isIngredientFractionConstraint,
  isIngredientRatioConstraint,
  isLabelFractionConstraint,
  isLabelIngredientCountConstraint,
} from "./constraint";

type TIngredientOrLabel = "ingredient" | "label";

/**
 * Takes an array of formulations constraints, separates them by type, and then
 * merges constraints of the same type on a per label/ingredient basis, ensuring
 * that no ingredient/label has more than one formulation constraint of the same
 * type.
 *
 * @param constraints formulations constraints
 */
export function consolidateFormulationConstraints(
  constraints: IFormulationConstraint[]
): IFormulationConstraint[] {
  const ingredientCountConstraints = constraints.filter(
    isIngredientCountConstraint
  );

  // IngredientCountConstraint for the entire DS - I guess there could technically
  // be more than 1 present
  const dsWideIngredientCountConstraints = ingredientCountConstraints.filter(
    (constraint) => !constraint.label
  );
  const ingredientCountConstraint =
    dsWideIngredientCountConstraints.length > 1
      ? consolidateConstraintSet(dsWideIngredientCountConstraints)
      : dsWideIngredientCountConstraints[0];

  const ingredientFractionConstraints = constraints.filter(
    isIngredientFractionConstraint
  );
  const labelIngredientCountConstraints = ingredientCountConstraints.filter(
    isLabelIngredientCountConstraint
  );
  const labelFractionConstraints = constraints.filter(
    isLabelFractionConstraint
  );

  const ingredientRatioConstraints = constraints.filter(
    isIngredientRatioConstraint
  );

  return [
    ...(ingredientCountConstraint ? [ingredientCountConstraint] : []),
    ...consolidateByIngredientOrLabel(
      ingredientFractionConstraints,
      "ingredient"
    ),
    ...consolidateByIngredientOrLabel(labelIngredientCountConstraints, "label"),
    ...consolidateByIngredientOrLabel(labelFractionConstraints, "label"),
    ...ingredientRatioConstraints,
  ];
}

/**
 * Ensures that no ingredients/labels have more than 1 formulation constraint
 * of a single type.
 *
 * @param constraints formulation constraints
 * @param ingredientOrLabel "ingredient" | "label"
 */
function consolidateByIngredientOrLabel<T extends IFormulationConstraint>(
  constraints: T[],
  ingredientOrLabel: Extract<keyof T, TIngredientOrLabel>
): T[] {
  // Creates a mapping of ingredients/labels to the constraints that are
  // affiliated with them.
  const groupedByKey = groupByIngredientOrLabel(constraints, ingredientOrLabel);

  return (Object.values(groupedByKey) as T[][]).map(consolidateConstraintSet);
}

/**
 * Consolidates an array of formulations constraints by merging constraints
 * together and taking the highest `min`, and lowest `max`.
 *
 * If dealing with formulation fraction constraints, then `is_required` is set
 * to true if any of the constraints are required.
 *
 * This behavior emulates what the backend does:
 * https://github.com/CitrineInformatics/mithril/blob/master/src/main/scala/io/citrine/nucleus/ml/sampler/library/mc/move/formulation/FormulationMove.scala#L442-L449
 *
 * @param constraintSet expected to have a length of at least 1, or else you can
 *                      end up getting undefined returned
 */
function consolidateConstraintSet<T extends IFormulationConstraint>(
  constraintSet: T[]
): T {
  if (constraintSet.length <= 1) {
    return constraintSet[0];
  } else {
    const min = Math.max(...constraintSet.map((c) => c.min));
    const max = Math.min(...constraintSet.map((c) => c.max));

    const clone = { ...constraintSet[0], min, max };

    if (isFormulationFractionConstraint(clone)) {
      clone.is_required = (
        constraintSet as TFormulationFractionConstraint[]
      ).some((c) => c.is_required);
    }

    return clone;
  }
}

/**
 * Groups formulation constraints by label or ingredient so that we can
 * subsequently consolidate them to ensure no ingredient/label has more than
 * one constraint of the same type
 *
 * @param constraints formulation constraints
 * @param ingredientOrLabel "ingredient" or "label"
 */
function groupByIngredientOrLabel<T extends IFormulationConstraint>(
  constraints: T[],
  ingredientOrLabel: Extract<keyof T, TIngredientOrLabel>
): Partial<Record<string, T[]>> {
  const mapping: Partial<Record<string, T[]>> = {};

  constraints.forEach((constraint) => {
    const name = constraint[ingredientOrLabel] as any as string | undefined;
    if (name) {
      if (!mapping[name]) {
        mapping[name] = [];
      }
      mapping[name]!.push(constraint);
    }
    return mapping;
  });

  return mapping;
}
